/**
 * Rows recording what the system did, not what anybody said. Drawn as their own
 * thread elements and never as a conversation preview. Each consumer spells the
 * list in its own dialect (Drizzle, PostgREST, a React switch) — the names come
 * from here so a new type cannot reach one and miss another.
 */
export const CONVERSATION_EVENT_TYPES = [
  'session_start',
  'handover_notified',
  'reply_skipped',
  'send_failed',
] as const

export type ConversationEventType = (typeof CONVERSATION_EVENT_TYPES)[number]

/** PostgREST `in` group — `not('message_type', 'in', CONVERSATION_EVENT_TYPES_SQL_GROUP)`. */
export const CONVERSATION_EVENT_TYPES_SQL_GROUP = `(${CONVERSATION_EVENT_TYPES.join(',')})`

/**
 * `reply_skipped` reason of an escalation that deliberately told the customer nothing.
 * Here and not with the other skip reasons because both inboxes, client and server, read it.
 */
export const SILENT_HANDOVER_SKIP_REASON = 'silent_handover'

/** Where a silent handover's marker keeps the escalation's own words beside that code. */
export const HANDOVER_REASON_PAYLOAD_KEY = 'handover_reason'

export function isConversationEventType(value: string): value is ConversationEventType {
  return (CONVERSATION_EVENT_TYPES as readonly string[]).includes(value)
}

/**
 * `reply_skipped` is written for a dozen machine reasons, and a paused conversation writes
 * one per inbound message. An operator sees two of them: the model's own decision to stay
 * silent (`ai_skip`), and an escalation that said nothing to the customer — without it no
 * reply carries the «Escalated» badge and the thread shows no sign of the handover.
 * Same rule in `thread-visibility.ts` (PostgREST) and the unified inbox source (SQL).
 */
export function isVisibleSkipMarker(payload: Record<string, unknown> | null | undefined): boolean {
  return payload?.ai_skip === true || payload?.reason === SILENT_HANDOVER_SKIP_REASON
}

/** Narrowed out of the record's jsonb, ready to draw. */
export interface ConversationEvent {
  type: ConversationEventType
  /** `session_start` only; null for the rest. */
  text: string | null
  /** Why it happened, where the record explains it. */
  reason: string | null
  /** The AI request behind a skipped reply, where there is one. */
  logId: string | null
  /** `reply_skipped` only: the AI escalated and deliberately sent the customer nothing. */
  silentHandover: boolean
  /** The customer message this decision answered — see `placeAfterAnsweredMessages`. */
  answeredMessageId: string | null
}

/** The jsonb narrowed once, so a renderer never sees `unknown`. */
export function toConversationEvent(row: {
  messageType: string
  contentText: string | null
  contentPayload: unknown
}): ConversationEvent | null {
  if (!isConversationEventType(row.messageType)) return null

  const payload = (row.contentPayload ?? {}) as Record<string, unknown>
  const silentHandover =
    row.messageType === 'reply_skipped' && payload.reason === SILENT_HANDOVER_SKIP_REASON
  // A silent handover's own `reason` is the machine code; the escalation's words ride beside it.
  const reason = silentHandover ? payload[HANDOVER_REASON_PAYLOAD_KEY] : payload.reason

  return {
    type: row.messageType,
    // Only a session names itself («Session #21 started»); the rest are worded
    // by the component that draws them.
    text: row.messageType === 'session_start' ? row.contentText?.trim() || null : null,
    reason: typeof reason === 'string' ? reason : null,
    logId: typeof payload.ai_request_log_id === 'string' ? payload.ai_request_log_id : null,
    silentHandover,
    answeredMessageId:
      typeof payload.answered_message_id === 'string' ? payload.answered_message_id : null,
  }
}

/**
 * A decision is written when the reply finishes, so a message the customer sent while
 * the AI was still thinking is stored before it. Drawn in write order, «AI chose not to
 * reply» sits under a message the AI never saw. Moves each event that names the message
 * it answered to right after that message, when the message is in view.
 */
export function placeAfterAnsweredMessages<T>(
  items: readonly T[],
  idOf: (item: T) => string,
  answeredMessageIdOf: (item: T) => string | null,
): T[] {
  // Only a row that stays put can be an anchor; one that moves would take its followers along.
  const anchors = new Set(items.filter((item) => !answeredMessageIdOf(item)).map(idOf))
  const placedAfter = new Map<string, T[]>()
  for (const item of items) {
    const answered = answeredMessageIdOf(item)
    if (answered && anchors.has(answered)) placedAfter.set(answered, [...(placedAfter.get(answered) ?? []), item])
  }
  if (placedAfter.size === 0) return [...items]

  const moved = new Set([...placedAfter.values()].flat())
  return items.flatMap((item) =>
    moved.has(item) ? [] : [item, ...(placedAfter.get(idOf(item)) ?? [])],
  )
}
